<template>
    <div class="login-page">
        <!-- Animated background -->
        <div class="bg-grid"></div>
        <div class="bg-glow bg-glow-1"></div>
        <div class="bg-glow bg-glow-2"></div>
        
        <div class="login-container">
            <div class="login-card">
                <!-- Logo and branding -->
                <div class="brand-section">
                    <img src="@/assets/segofy-logo.png" alt="Segofy" class="logo" />
                    <p class="tagline">Website health monitoring made simple</p>
                </div>

                <!-- Login Form -->
                <form v-if="!showForgotPassword" name="login-form" @submit.prevent="handleSubmit">
                    <!-- Email field -->
                    <div class="input-group">
                        <label for="email">Email</label>
                        <div class="input-wrapper">
                            <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                                <polyline points="22,6 12,13 2,6"/>
                            </svg>
                            <input 
                                type="email" 
                                id="email" 
                                v-model="input.email" 
                                autocomplete="email"
                                placeholder="you@example.com"
                                required
                            />
                        </div>
                    </div>

                    <!-- Password field -->
                    <div class="input-group">
                        <label for="password">Password</label>
                        <div class="input-wrapper">
                            <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                            </svg>
                            <input 
                                type="password" 
                                id="password" 
                                v-model="input.password"
                                placeholder="••••••••"
                                required
                            />
                        </div>
                    </div>

                    <!-- Forgot password link -->
                    <div class="forgot-password-link">
                        <a href="#" @click.prevent="showForgotPasswordForm">Forgot password?</a>
                    </div>

                    <!-- Success message -->
                    <Transition name="fade">
                        <div v-if="successMessage" class="message success-message">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                                <polyline points="22 4 12 14.01 9 11.01"/>
                            </svg>
                            <span>{{ successMessage }}</span>
                        </div>
                    </Transition>

                    <!-- Error message -->
                    <Transition name="fade">
                        <div v-if="error" class="message error-message">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="15" y1="9" x2="9" y2="15"/>
                                <line x1="9" y1="9" x2="15" y2="15"/>
                            </svg>
                            <span>{{ error }}</span>
                        </div>
                    </Transition>

                    <!-- Login button -->
                    <button class="btn-primary" type="submit" :disabled="loading">
                        <span v-if="loading" class="spinner"></span>
                        <span v-else>Sign In</span>
                    </button>

                    <!-- Divider -->
                    <div class="divider">
                        <span>or continue with</span>
                    </div>

                    <!-- Google button -->
                    <button type="button" class="btn-google" @click="handleGoogleLogin" :disabled="loading">
                        <svg class="google-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                        </svg>
                        <span>Google</span>
                    </button>
                </form>

                <!-- Forgot Password Form -->
                <form v-else name="forgot-password-form" @submit.prevent="handleForgotPassword">
                    <div class="forgot-password-header">
                        <h2>Reset Password</h2>
                        <p>Enter your email address and we'll send you a link to reset your password.</p>
                    </div>

                    <!-- Email field -->
                    <div class="input-group">
                        <label for="forgot-email">Email</label>
                        <div class="input-wrapper">
                            <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                                <polyline points="22,6 12,13 2,6"/>
                            </svg>
                            <input 
                                type="email" 
                                id="forgot-email" 
                                v-model="forgotPasswordEmail" 
                                autocomplete="email"
                                placeholder="you@example.com"
                                required
                            />
                        </div>
                    </div>

                    <!-- Success message -->
                    <Transition name="fade">
                        <div v-if="forgotPasswordSuccess" class="message success-message">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                                <polyline points="22 4 12 14.01 9 11.01"/>
                            </svg>
                            <span>{{ forgotPasswordSuccess }}</span>
                        </div>
                    </Transition>

                    <!-- Error message -->
                    <Transition name="fade">
                        <div v-if="forgotPasswordError" class="message error-message">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="15" y1="9" x2="9" y2="15"/>
                                <line x1="9" y1="9" x2="15" y2="15"/>
                            </svg>
                            <span>{{ forgotPasswordError }}</span>
                        </div>
                    </Transition>

                    <!-- Submit button -->
                    <button class="btn-primary" type="submit" :disabled="forgotPasswordLoading">
                        <span v-if="forgotPasswordLoading" class="spinner"></span>
                        <span v-else>Send Reset Link</span>
                    </button>

                    <!-- Back to login link -->
                    <div class="back-to-login">
                        <a href="#" @click.prevent="hideForgotPasswordForm">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
                                <polyline points="15 18 9 12 15 6"/>
                            </svg>
                            Back to login
                        </a>
                    </div>
                </form>

                <!-- Register link -->
                <div class="register-link">
                    <span>Don't have an account?</span>
                    <router-link to="/register">Create one</router-link>
                </div>
            </div>

            <!-- Footer -->
            <p class="footer-text">
                Secure login protected by industry-standard encryption
            </p>
        </div>
    </div>
</template>

<script>
import { api } from '../utils/api'

export default {
    name: 'LoginView',
    inject: ['env'],
    data() {
        return {
            input: {
                email: "",
                password: ""
            },
            loading: false,
            error: null,
            successMessage: null,
            // Forgot password state
            showForgotPassword: false,
            forgotPasswordEmail: "",
            forgotPasswordLoading: false,
            forgotPasswordError: null,
            forgotPasswordSuccess: null
        }
    },
    created() {
        const token = (() => { try { return localStorage.getItem('fic') } catch (_) { return null } })()
        if (token) {
            window.location.replace('/domains')
        }
        // Check for password change success
        if (this.$route.query['password-change'] === 'success') {
            this.successMessage = 'Your password has been successfully updated. Please log in with your new password.'
        }
    },
    methods: {
        async handleSubmit() {
            this.loading = true
            this.error = null

            try {
                // Use centralized API utility with skipAuthCheck set to true
                const loginResponse = await api('/login', {
                    method: 'POST',
                    body: JSON.stringify(this.input)
                }, this.$router, { skipAuthCheck: true })

                // Expect access_token in response
                const token = loginResponse && (loginResponse.accessToken || loginResponse.token)
                if (token) {
                    try { localStorage.setItem('fic', token) } catch (_) { /* ignore storage errors */ }
                }

                // Force navigation to domains page
                window.location.replace('/domains')
            } catch (error) {
                // Use code from error response if available, otherwise use default message
                this.error = error.code || 'Something went wrong please contact support'
            } finally {
                this.loading = false
            }
        },

        async handleGoogleLogin() {
            this.loading = true
            this.error = null

            try {
                // Request Google OAuth URL from the backend
                const response = await api('/auth/google/login', {
                    method: 'GET'
                }, this.$router, { skipAuthCheck: true })

                if (response && response.authUrl && response.state) {
                    // Store the state in session storage for CSRF verification
                    try {
                        sessionStorage.setItem('oauth_state', response.state)
                    } catch (storageError) {
                        console.error('Failed to store OAuth state:', storageError)
                        this.error = 'Failed to store session data. Please check browser settings.'
                        this.loading = false
                        return
                    }

                    // Redirect to Google OAuth consent screen
                    window.location.href = response.authUrl
                } else {
                    throw new Error('No auth URL or state received from server')
                }
            } catch (error) {
                // Use code from error response if available, otherwise use default message
                this.error = error.code || 'Something went wrong please contact support'
                console.error('Google login error:', error)
                this.loading = false
            }
        },

        showForgotPasswordForm() {
            this.showForgotPassword = true
            this.forgotPasswordEmail = this.input.email || ""
            this.forgotPasswordError = null
            this.forgotPasswordSuccess = null
            this.error = null
            this.successMessage = null
        },

        hideForgotPasswordForm() {
            this.showForgotPassword = false
            this.forgotPasswordError = null
            this.forgotPasswordSuccess = null
        },

        async handleForgotPassword() {
            this.forgotPasswordLoading = true
            this.forgotPasswordError = null
            this.forgotPasswordSuccess = null

            try {
                await api('/user/forgot-password', {
                    method: 'POST',
                    body: JSON.stringify({ email: this.forgotPasswordEmail })
                }, this.$router, { skipAuthCheck: true })

                this.forgotPasswordSuccess = 'If an account exists with this email, you will receive a password reset link shortly.'
                this.forgotPasswordEmail = ""
            } catch (error) {
                this.forgotPasswordError = error.code || 'Something went wrong. Please try again later.'
            } finally {
                this.forgotPasswordLoading = false
            }
        }
    }
}
</script>

<style scoped>
.login-page {
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    inset: 0;
    overflow: hidden;
    background: linear-gradient(135deg, #0a0a0a 0%, #151520 50%, #0d0d15 100%);
}

/* Animated grid background */
.bg-grid {
    position: absolute;
    inset: 0;
    background-image: 
        linear-gradient(rgba(60, 98, 230, 0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(60, 98, 230, 0.03) 1px, transparent 1px);
    background-size: 60px 60px;
    mask-image: radial-gradient(ellipse at center, black 0%, transparent 75%);
    animation: gridPulse 8s ease-in-out infinite;
}

@keyframes gridPulse {
    0%, 100% { opacity: 0.5; }
    50% { opacity: 1; }
}

/* Floating glow effects */
.bg-glow {
    position: absolute;
    border-radius: 50%;
    filter: blur(100px);
    opacity: 0.4;
    animation: float 15s ease-in-out infinite;
}

.bg-glow-1 {
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(60, 98, 230, 0.3) 0%, transparent 70%);
    top: -200px;
    right: -100px;
    animation-delay: 0s;
}

.bg-glow-2 {
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(94, 194, 105, 0.2) 0%, transparent 70%);
    bottom: -150px;
    left: -100px;
    animation-delay: -7s;
}

@keyframes float {
    0%, 100% { transform: translate(0, 0) scale(1); }
    33% { transform: translate(30px, -30px) scale(1.05); }
    66% { transform: translate(-20px, 20px) scale(0.95); }
}

.login-container {
    position: relative;
    z-index: 10;
    width: 100%;
    max-width: 420px;
    padding: 20px;
    animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.login-card {
    background: rgba(24, 24, 25, 0.8);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 20px;
    padding: 40px 36px;
    box-shadow: 
        0 25px 50px -12px rgba(0, 0, 0, 0.5),
        0 0 0 1px rgba(255, 255, 255, 0.05) inset;
}

/* Brand section */
.brand-section {
    text-align: center;
    margin-bottom: 36px;
}

.logo {
    height: 36px;
    width: auto;
    margin-bottom: 12px;
}

.tagline {
    color: var(--text-secondary);
    font-size: 0.9rem;
    margin: 0;
    letter-spacing: 0.02em;
}

/* Form elements */
.input-group {
    margin-bottom: 20px;
}

.input-group label {
    display: block;
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--text-secondary);
    margin-bottom: 8px;
    letter-spacing: 0.02em;
}

.input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.input-icon {
    position: absolute;
    left: 14px;
    width: 18px;
    height: 18px;
    color: var(--text-muted);
    pointer-events: none;
    transition: color 0.2s ease;
}

.input-wrapper:focus-within .input-icon {
    color: var(--accent-color);
}

.input-group input {
    width: 100%;
    padding: 14px 14px 14px 46px;
    font-size: 0.95rem;
    font-family: inherit;
    color: var(--text-primary);
    background: rgba(61, 61, 61, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    outline: none;
    transition: all 0.2s ease;
}

.input-group input::placeholder {
    color: var(--text-muted);
}

.input-group input:hover {
    border-color: rgba(255, 255, 255, 0.15);
    background: rgba(61, 61, 61, 0.4);
}

.input-group input:focus {
    border-color: var(--accent-color);
    background: rgba(61, 61, 61, 0.5);
    box-shadow: 0 0 0 3px rgba(60, 98, 230, 0.15);
}

/* Messages */
.message {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 14px 16px;
    border-radius: 12px;
    margin-bottom: 20px;
    font-size: 0.9rem;
    line-height: 1.5;
}

.message svg {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    margin-top: 2px;
}

.success-message {
    background: rgba(94, 194, 105, 0.1);
    border: 1px solid rgba(94, 194, 105, 0.2);
    color: var(--success-color);
}

.error-message {
    background: rgba(221, 82, 76, 0.1);
    border: 1px solid rgba(221, 82, 76, 0.2);
    color: var(--error-color);
}

/* Buttons */
.btn-primary {
    width: 100%;
    padding: 14px 24px;
    font-size: 1rem;
    font-weight: 600;
    font-family: inherit;
    color: white;
    background: linear-gradient(135deg, var(--accent-color) 0%, #2a4bc4 100%);
    border: none;
    border-radius: 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.2s ease;
    box-shadow: 0 4px 15px rgba(60, 98, 230, 0.3);
}

.btn-primary:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(60, 98, 230, 0.4);
}

.btn-primary:active:not(:disabled) {
    transform: translateY(0);
}

.btn-primary:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

/* Spinner */
.spinner {
    width: 20px;
    height: 20px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

/* Divider */
.divider {
    display: flex;
    align-items: center;
    gap: 16px;
    margin: 24px 0;
}

.divider::before,
.divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
}

.divider span {
    font-size: 0.8rem;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.1em;
    white-space: nowrap;
}

/* Google button */
.btn-google {
    width: 100%;
    padding: 14px 24px;
    font-size: 0.95rem;
    font-weight: 500;
    font-family: inherit;
    color: var(--text-primary);
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    transition: all 0.2s ease;
}

.btn-google:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.15);
}

.btn-google:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.google-icon {
    width: 20px;
    height: 20px;
}

/* Forgot password link */
.forgot-password-link {
    text-align: right;
    margin-top: -12px;
    margin-bottom: 20px;
}

.forgot-password-link a {
    font-size: 0.85rem;
    color: var(--accent-color);
    text-decoration: none;
    transition: color 0.2s ease;
}

.forgot-password-link a:hover {
    color: #5a7ff0;
    text-decoration: underline;
}

/* Forgot password form header */
.forgot-password-header {
    text-align: center;
    margin-bottom: 28px;
}

.forgot-password-header h2 {
    font-size: 1.4rem;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 10px 0;
}

.forgot-password-header p {
    font-size: 0.9rem;
    color: var(--text-secondary);
    margin: 0;
    line-height: 1.5;
}

/* Back to login link */
.back-to-login {
    text-align: center;
    margin-top: 20px;
}

.back-to-login a {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.9rem;
    color: var(--text-secondary);
    text-decoration: none;
    transition: color 0.2s ease;
}

.back-to-login a:hover {
    color: var(--accent-color);
}

.back-to-login a svg {
    transition: transform 0.2s ease;
}

.back-to-login a:hover svg {
    transform: translateX(-3px);
}

/* Register link */
.register-link {
    text-align: center;
    margin-top: 28px;
    padding-top: 24px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
}

.register-link span {
    color: var(--text-secondary);
}

.register-link a {
    color: var(--accent-color);
    text-decoration: none;
    font-weight: 500;
    transition: color 0.2s ease;
}

.register-link a:hover {
    color: #5a7ff0;
}

/* Footer text */
.footer-text {
    text-align: center;
    margin-top: 24px;
    font-size: 0.75rem;
    color: var(--text-muted);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
    transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}

/* Responsive */
@media (max-width: 768px) {
    .login-container {
        padding: 12px;
    }

    .login-card {
        padding: 28px 24px;
        border-radius: 16px;
    }

    .brand-section {
        margin-bottom: 24px;
    }

    .logo {
        height: 32px;
    }

    .tagline {
        font-size: 0.85rem;
    }

    .input-group {
        margin-bottom: 16px;
    }

    .input-group input {
        padding: 12px 12px 12px 42px;
    }

    .btn-primary,
    .btn-google {
        padding: 12px 20px;
    }

    .divider {
        margin: 20px 0;
    }

    .register-link {
        margin-top: 20px;
        padding-top: 18px;
    }

    .footer-text {
        margin-top: 16px;
        font-size: 0.7rem;
    }

    .bg-glow-1 {
        width: 300px;
        height: 300px;
    }

    .bg-glow-2 {
        width: 250px;
        height: 250px;
    }
}

@media (max-width: 480px) {
    .login-container {
        padding: 8px;
    }

    .login-card {
        padding: 24px 20px;
    }

    .brand-section {
        margin-bottom: 20px;
    }

    .logo {
        height: 28px;
        margin-bottom: 8px;
    }

    .tagline {
        font-size: 0.8rem;
    }

    .input-group label {
        font-size: 0.8rem;
        margin-bottom: 6px;
    }

    .message {
        padding: 12px 14px;
        font-size: 0.85rem;
    }

    .register-link {
        margin-top: 18px;
        padding-top: 16px;
        font-size: 0.85rem;
    }

    .footer-text {
        margin-top: 12px;
    }
}
</style>
